import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes"

export default class extends Controller {
  static targets = ["display", "editor", "input", "multiSelect", "loadingIndicator"]
  static values = {
    fieldType: { type: String, default: "text" },
    fieldKey: String,
    updateUrl: String
  }

  connect() {
    this.isEditing = false
    this.keydownSetup = false
    this.submitting = false
  }

  startEdit(event) {
    event.preventDefault()
    if (this.isEditing) return

    this.isEditing = true
    this.displayTarget.classList.add("hidden")
    this.editorTarget.classList.remove("hidden")
    this.captureEditSnapshot()

    requestAnimationFrame(() => {
      if (this.fieldTypeValue === "simple-select") {
        const tomSelectInput = this.editorTarget.querySelector(".ts-control input")
        if (tomSelectInput) {
          tomSelectInput.focus()
          this.setupKeydown(tomSelectInput)
        }
      } else if (this.hasInputTarget) {
        this.inputTarget.focus()
      }
    })
  }

  cancel(event) {
    if (event) event.preventDefault()
    if (this.submitting) return

    this.restoreEditSnapshot()

    this.isEditing = false
    this.editorTarget.classList.add("hidden")
    this.displayTarget.classList.remove("hidden")
  }

  async submit(event) {
    if (event) event.preventDefault()
    if (this.submitting) return

    let value
    if (this.fieldTypeValue === "simple-select") {
      value = this.getMultiSelectValues()
    } else if (this.hasInputTarget) {
      value = this.inputTarget.value
    }

    this.submitting = true
    this.setBusy(true)

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({
          field_key: this.fieldKeyValue,
          value: value
        })
      })

      const isTurboStream = response.headers.get("content-type")?.includes("text/vnd.turbo-stream.html")

      if (isTurboStream) {
        Turbo.renderStreamMessage(await response.text())
      } else if (!response.ok) {
        this.handleError(response.status)
      }
    } catch (error) {
      this.handleError(0, error.message)
    } finally {
      this.submitting = false
      this.setBusy(false)
    }
  }

  setBusy(busy) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle(
        "radar-interface__field-editor-input-row__status--loading",
        busy,
      )
    }

    this.editorTarget.setAttribute("aria-busy", busy ? "true" : "false")

    this.editorTarget.querySelectorAll(".button-group button").forEach((btn) => {
      btn.disabled = busy
    })
  }

  handleError(status, message = null) {
    let errorMessage

    switch (status) {
      case 401:
      case 403:
        errorMessage = "You don't have permission to update this field."
        break
      case 404:
        errorMessage = "Device not found."
        break
      case 422:
        errorMessage = "Invalid value provided."
        break
      case 500:
        errorMessage = "Server error. Please try again."
        break
      case 0:
        errorMessage = message || "Network error. Please check your connection."
        break
      default:
        errorMessage = "Failed to save. Please try again."
    }

    flash(errorMessage, "error")
  }

  setupKeydown(tomSelectInput) {
    if (this.keydownSetup) return
    this.keydownSetup = true

    tomSelectInput.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        this.cancel(event)
      } else if (event.key === "Enter") {
        const hasText = tomSelectInput.value.trim() !== ""

        if (!hasText) {
          event.preventDefault()
          event.stopPropagation()
          this.submit(event)
        }
      }
    }, { capture: true })
  }

  captureEditSnapshot() {
    if (this.fieldTypeValue === "simple-select") {
      this.editSnapshot = this.getMultiSelectValues()
    } else if (this.hasInputTarget) {
      this.editSnapshot = this.inputTarget.value
    }
  }

  restoreEditSnapshot() {
    if (this.fieldTypeValue === "simple-select") {
      this.restoreMultiSelectValues(this.editSnapshot ?? [])
    } else if (this.hasInputTarget) {
      this.inputTarget.value = this.editSnapshot ?? ""
    }
  }

  getSimpleSelectController() {
    if (!this.hasMultiSelectTarget) return null

    const simpleSelectWrapper = this.multiSelectTarget.closest("[data-controller~='simple-select']")
    if (!simpleSelectWrapper) return null

    return this.application.getControllerForElementAndIdentifier(
      simpleSelectWrapper,
      "simple-select"
    )
  }

  restoreMultiSelectValues(values) {
    const simpleSelectController = this.getSimpleSelectController()
    if (!simpleSelectController?.select) return

    const { select } = simpleSelectController
    select.clear()
    values.forEach((value) => select.addItem(value))
    select.setTextboxValue("")
  }

  getMultiSelectValues() {
    const simpleSelectController = this.getSimpleSelectController()
    if (!simpleSelectController?.select) return []

    return simpleSelectController.select.items.slice()
  }
}
